import { BehaviorSubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import { MATOMO_CONFIG, type MatomoConfig } from 'src/app/matomo/matomo.config';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';

export type MatomoConsentDecision = 'unknown' | 'accepted' | 'rejected';

const STORAGE_KEY = 'nakos.matomo.consent.v1';
// Re-ask after six months rather than treating an old choice as permanent.
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const CONSENT_LIFETIME_MS = 180 * 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class MatomoConsentService {
  public readonly available: boolean;
  public readonly decision$: Observable<MatomoConsentDecision>;
  private readonly decision: BehaviorSubject<MatomoConsentDecision>;

  constructor(@Inject(DOCUMENT) private document: Document,
    @Inject(MATOMO_CONFIG) config: MatomoConfig | null) {
    this.available = !!config?.enabled && isPublicWebAppBuild() && !Capacitor.isNativePlatform()
      && !!this.document.defaultView && config.allowedHosts.includes(this.document.location.hostname);
    this.decision = new BehaviorSubject<MatomoConsentDecision>(this.available
      ? this.readDecision()
      : 'unknown');
    this.decision$ = this.decision.asObservable();
  }

  public getDecision(): Observable<MatomoConsentDecision> {
    return this.decision$;
  }

  public setDecision(value: 'accepted' | 'rejected'): void {
    if (!this.available) {
      return;
    }

    try {
      this.document.defaultView!.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        decision: value,
        expiresAt: Date.now() + CONSENT_LIFETIME_MS,
      }));
    } catch {
      // Storage may be unavailable; the choice still applies for this page session.
    }

    this.decision.next(value);
  }

  private readDecision(): MatomoConsentDecision {
    try {
      const raw = this.document.defaultView!.localStorage.getItem(STORAGE_KEY);
      const record = raw
        ? JSON.parse(raw) as { decision?: string; expiresAt?: number }
        : null;

      if (record && (record.decision === 'accepted' || record.decision === 'rejected')
        && typeof record.expiresAt === 'number' && record.expiresAt > Date.now()) {
        return record.decision;
      }
    } catch {
      // Missing, invalid, or unavailable storage means consent has not been granted.
    }

    return 'unknown';
  }
}
